import { ChangeDetectorRef, Component, Output, EventEmitter } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Chart, ChartOptions, ChartType } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { MasterService } from '../../../@core/services/master.service';
import * as echarts from 'echarts'
import { RoleService } from '../../../@core/services/role.service';

Chart.register(ChartDataLabels);

@Component({
  selector: 'ngx-analysis',
  templateUrl: './analysis.component.html',
  styleUrls: ['./analysis.component.scss']
})

export class AnalysisComponent {
  userRole: any;
  projectHistory: any[] = [];

  // Output events for component communication
  @Output() currencySelected = new EventEmitter<string>();
  @Output() partSelected = new EventEmitter<string>();
  @Output() vendorSelected = new EventEmitter<string>();

  constructor(private f_builder: FormBuilder,
    private router: Router,
    private modalService: NgbModal,
    private masterService: MasterService,
    private notify: NbToastrService, private roleService: RoleService,
  ) {

  }

  // Metrics
  // metrics = {
  //   completion: 88,
  //   totalParts: 42,
  //   closedParts: 37
  // };
  metrics: { projects: any; parts: any; openProjects: any; totalValue: string; closedParts: any; completionPercentage: any; } = {
    projects: 0,
    parts: 0,
    openProjects: 0,
    totalValue: '0',
    closedParts: 0,
    completionPercentage: 0
  };
  project_id: any;
  version_id!: number;
  procurement_version_id: number = 0;

  // Cost Pie Chart
  costChartType: ChartType = 'doughnut';

  costChartData: any = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1
      }
    ]
  };

  offlineOnlineChartData: any = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1
      }
    ]
  };

  readonly costColors: string[] = [
    'rgba(59, 130, 246, 0.92)',
    'rgba(16, 185, 129, 0.88)',
    'rgba(249, 115, 22, 0.88)',
    'rgba(168, 85, 247, 0.84)',
    'rgba(14, 165, 233, 0.88)',
    'rgba(234, 179, 8, 0.88)'
  ];

  readonly offlineOnlineColors: string[] = [
    'rgba(37, 106, 235, 0.95)',
    'rgba(46, 247, 213, 0.95)'
  ];

  loadCostByCurrency() {
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {

      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      "project_id": Number(this.project_id),
      "version_id": Number(this.version_id),
    };

    // Add date filter parameters if set
    if (this.costStartDate) {
      params.start_date = this.costStartDate;
    }
    if (this.costEndDate) {
      params.end_date = this.costEndDate;
    }

    this.masterService.getCostByCurrency(params).subscribe({
      next: (res: any) => {

        const split = res?.currency_split || [];

        const labels: string[] = [];
        const data: number[] = [];

        split.forEach((row: any) => {
          labels.push(row.currency || 'Unknown');
          data.push(Number(row.amount) || 0);
        });

        const colors = this.generateColors(data.length, this.costColors);
        const borders = colors.map((color: string) => color.replace(/rgba\((\d+),(\d+),(\d+),(.*)\)/, 'rgba($1,$2,$3,1)'));

        this.costChartData = {
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
              borderColor: borders,
              borderWidth: 1,
              hoverOffset: 10,
              borderRadius: 8
            }
          ]
        };
      },
      error: () => {
        this.costChartData = {
          labels: [],
          datasets: [{ data: [] }]
        };
      }
    });
  }
  generateColors(count: number, palette: string[] = []): string[] {
    const base = palette.length > 0 ? palette : [
      '#60a5fa',
      '#2563eb',
      '#1e40af',
      '#1d4ed8',
      '#3b82f6'
    ];

    return Array.from({ length: count }, (_, i) => base[i % base.length]);
  }

  formatToIndianUnit(value: number): string {

    if (value >= 10000000) {       // 1 crore
      return (value / 10000000).toFixed(2) + ' Cr';
    }

    if (value >= 100000) {         // 1 lakh
      return (value / 100000).toFixed(2) + ' L';
    }

    return value.toString();
  }
  // Commodity vs Region vs Value Chart
  commodityChartData = {
    labels: ['US region', 'Other Asia', 'Europe region', 'India'],
    datasets: [
      {
        label: 'Contacts',
        data: [5, 3, 4.5, 2],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
      {
        label: 'Connection elements',
        data: [4, 2, 3.5, 1.5],
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Connectors',
        data: [3, 1.5, 2.8, 1],
        backgroundColor: 'rgba(255, 159, 64, 0.8)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
      },
      {
        label: 'Insulators',
        data: [2, 1, 1.8, 0.8],
        backgroundColor: 'rgba(155, 89, 182, 0.8)',
        borderColor: 'rgba(155, 89, 182, 1)',
        borderWidth: 1,
      },
    ],
  };
  commodityChartType: ChartType = 'bar';

  // Payment Term Table
  // paymentTerms = [
  //   { term: 'Credit', value: 280000, percentage: 72 },
  //   { term: 'Advance', value: 85000, percentage: 28 },
  // ];

  // Offline/Online Chart
  offlineOnlineChartType: ChartType = 'bar';

  // Key Vendors Table
  keyVendors = [
    { name: 'RS Compo..', value: 72000, percentage: 25 },
    { name: 'RayQ', value: 50005, percentage: 18 },
    { name: 'Mouser...', value: 38080, percentage: 14 },
    { name: 'RJ Cables...', value: 23000, percentage: 9 },
    { name: 'All India', value: 15000, percentage: 6 },
  ];

  // Project History Table
  // projectHistory = [
  //   { title: 'IPN-0051H have added to the project' },
  //   { title: 'Project assigned to shiva on 26th of January 2026' },
  //   { title: 'Project created on 25th of January by rama.' },
  // ];

  // Part Details Table
  // partDetails = [
  //   { part: 'IPN-0017', deviation: 3090, refPart: 'IPN-0014', lt: '+21', quantityDiffPart: 'IPN-0017', quantityDiff: '-80' },
  //   { part: 'IPN-0101', deviation: 2190, refPart: 'IPN01045', lt: '+4', quantityDiffPart: 'IPN-0101', quantityDiff: '-300' },
  //   { part: 'IPN-1576', deviation: 870, refPart: 'IPN-1345', lt: '+4', quantityDiffPart: 'IPN-1576', quantityDiff: '-8' },
  //   { part: 'IPN889', deviation: 490, refPart: 'IPN4456', lt: '+3', quantityDiffPart: 'IPN889', quantityDiff: '-5' },
  //   { part: 'IPN00256', deviation: 250, refPart: 'IPN0566', lt: '+2', quantityDiffPart: 'IPN0025 6', quantityDiff: '-1' },
  // ];
  partDetails: any[] = [];

  // chartOptions: ChartOptions = {
  //   responsive: true,
  //   maintainAspectRatio: false,
  //   plugins: {
  //     legend: {
  //       position: 'bottom'
  //     }
  //   }
  // };
  // chartOptions: ChartOptions = {
  //   responsive: true,
  //   maintainAspectRatio: false,
  //   plugins: {
  //     legend: {
  //       position: 'bottom'
  //     },

  //     // ✅ value graph midha chupinchadaniki
  //     datalabels: {
  //       color: '#1e3a8a',
  //       font: {
  //         weight: 'bold',
  //         size: 12
  //       },
  //       formatter: (value: any) => {
  //         return this.formatToIndianUnit(value);
  //       }
  //     }
  //   },
  //   onClick: (event: any, elements: any, chart: any) => {
  //     if (elements && elements.length > 0) {
  //       const clickedIndex = elements[0].index;
  //       const currency = this.costChartData.labels[clickedIndex];
  //       console.log("📊 Currency chart clicked - Currency:", currency);
  //       this.onCurrencyClick(currency);
  //     }
  //   }
  // };
  // ...existing code...
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            weight: '600',
            family: "'Inter', sans-serif"
          },
          color: '#334155'
        }
      },
      datalabels: {
        color: '#0f172a',
        font: {
          weight: 700,
          size: 12,
          family: "'Inter', sans-serif"
        },
        anchor: 'end',
        align: 'top',
        offset: -14,
        clamp: true,
        display: 'auto',
        formatter: (value: any) => {
          return this.formatToIndianUnit(value);
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        padding: 12,
        cornerRadius: 10,
        titleFont: {
          size: 13,
          weight: '700'
        },
        bodyFont: {
          size: 12
        },
        borderColor: 'rgba(148, 163, 184, 0.2)',
        borderWidth: 1
      }
    },
    layout: {
      padding: {
        top: 22,
        bottom: 8,
        left: 10,
        right: 10
      }
    },
    onClick: (event: any, elements: any, chart: any) => {
      if (elements && elements.length > 0) {
        const clickedIndex = elements[0].index;
        const currency = this.costChartData.labels[clickedIndex];
        console.log("📊 Currency chart clicked - Currency:", currency);
        this.onCurrencyClick(currency);
      }
    }
  };
  // ...existing code...
  chartLegend = true;
  ngOnInit(): void {
    this.userRole = this.roleService.getUserRole();
    this.project_id = sessionStorage.getItem('project_id');
    this.version_id = Number(sessionStorage.getItem('version_id'));
    this.procurement_version_id = Number(sessionStorage.getItem('procurement_version_id')) || 0;
    // console.log('\u2713 Analysis Component initialized with:');
    console.log('  - project_id:', this.project_id);
    console.log('  - version_id:', this.version_id);
    console.log('  - procurement_version_id:', this.procurement_version_id);
    console.log('  - userRole:', this.userRole);

    // Get engineer_id and manager_id from sessionStorage (set by project-view component)
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const payload = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      "project_id": Number(this.project_id),
      "version_id": Number(this.version_id),
      "procurement_version_id": this.procurement_version_id,

    };

    console.log('Dashboard payload =>', payload);


    this.loadDashboard(payload);
    this.getOfflineOnlineSummary();
    this.loadMasterData();
    this.loadPaymentSummary();
    this.loadPartPricingSummary();
    this.loadCostByCurrency();
    this.loadQuantityDiff();
    this.loadPartLeadtime();
    this.getNotifications();
  }



  // getNotifications() {
  //   const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
  //   const userId = currentUser?.user_id;

  //   // ✅ Use same values from ngOnInit (already correct)
  //   const projectId = Number(this.project_id);
  //   const versionId = Number(this.version_id);

  //   console.log('projectId:', projectId);
  //   console.log('versionId:', versionId);

  //   this.masterService.fetchNotifications(userId).subscribe({
  //     next: (res: any) => {
  //       console.log('API Response:', res);

  //       if (res?.status === 'success') {

  //         // 🔥 Debug each row comparison
  //         res.data.forEach((item: any) => {
  //           console.log(
  //             'API:',
  //             item.project_id, item.version_id,
  //             '| SESSION:',
  //             projectId, versionId
  //           );
  //         });

  //         // ✅ Correct filter
  //         const filteredData = res.data.filter((item: any) =>
  //           Number(item.project_id) === projectId &&
  //           Number(item.version_id) === versionId
  //         );

  //         console.log('Filtered Data:', filteredData);

  //         // ✅ IMPORTANT: use filteredData (not res.data)
  //         this.projectHistory = filteredData.map((item: any) => ({
  //           title: item.message
  //         }));

  //         console.log('Final UI Data:', this.projectHistory);
  //       }
  //     },
  //     error: (err) => {
  //       console.error('Error fetching notifications', err);
  //     }
  //   });
  // }
  getNotifications() {
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userId = currentUser?.user_id;

    const projectId = Number(this.project_id);
    const versionId = Number(this.version_id);

    this.masterService.fetchNotifications(userId).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {

          const filteredData = res.data.filter((item: any) =>
            Number(item.project_id) === projectId &&
            Number(item.version_id) === versionId
          );

          this.projectHistory = filteredData.map((item: any) => ({
            title: item.message
          }));
        }
      },
      error: (err) => {
        console.error('Error fetching notifications', err);
      }
    });
  }
  getOfflineOnlineSummary() {

    // Get engineer_id and manager_id from sessionStorage
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {
      project_id: Number(this.project_id),
      version_id: Number(this.version_id),
      engineer_id: engineer_id || currentUser.engineer_id,
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id

    };

    // Add date filter parameters if set
    if (this.offlineOnlineStartDate) {
      params.start_date = this.offlineOnlineStartDate;
    }
    if (this.offlineOnlineEndDate) {
      params.end_date = this.offlineOnlineEndDate;
    }

    this.masterService
      .getOfflineOnlineSummary(params)
      .subscribe((res: any) => {

        const values = [res.offline?.count || 0, res.online?.count || 0];
        const dataLabels = ['Offline', 'Online'];
        const colors = this.offlineOnlineColors.slice(0, values.length);

        this.offlineOnlineChartData = {
          labels: dataLabels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: colors.map(color => color.replace(/rgba\((\d+),(\d+),(\d+),(.*)\)/, 'rgba($1,$2,$3,1)')),
              borderWidth: 1,
              borderRadius: 6,
              maxBarThickness: 45
            }
          ]
        } as any;

      });


  }
  // home() {
  //   this.router.navigate(['/dashboard-engineer'])
  // }
  home() {

    if (this.userRole === 'Engineer') {
      this.router.navigate(['/dashboard-engineer'])
    }
    else if (this.userRole === 'Manager' || this.userRole === 'Admin') {
      this.router.navigate(['/dashboard-manager'])
    }
  }
  loadDashboard(payload: any) {

    this.masterService.dashboardHierarchy(payload).subscribe({
      next: (res: any) => {

        this.metrics = {
          projects: res?.no_of_projects || 0,
          parts: res?.no_of_parts || 0,
          openProjects: res?.no_of_open_projects || 0,
          totalValue: this.formatIndianCurrency(res?.total_sourced_value),
          closedParts: res?.closed_parts,
          completionPercentage: res?.completion_percentage
        };

      },
      error: (err) => {
        console.error('Dashboard API error', err);
      }
    });

  }
  formatIndianCurrency(value: number | null | undefined): string {

    if (!value && value !== 0) {
      return '0';
    }

    return Math.round(value).toLocaleString('en-IN');
  }
  masterDataList: any[] = [];
  showTopVendorFilter = false;
  selectedTopN = 5;

  toggleTopVendorFilter() {
    this.showTopVendorFilter = !this.showTopVendorFilter;
  }
  loadMasterData() {

    // Get engineer_id and manager_id from sessionStorage
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const payload: any = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      project_id: Number(this.project_id),
      version_id: Number(this.version_id),
      top_n: this.selectedTopN
    };

    this.masterService.getTopSuppliers(payload)
      .subscribe((res: any) => {

        this.masterDataList = res?.suppliers || [];

      });
  }
  paymentTerms: any[] = [];

  loadPaymentSummary() {

    // Get engineer_id and manager_id from sessionStorage
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      project_id: Number(this.project_id),
      version_id: Number(this.version_id)
    };



    this.masterService.getSupplierPaymentSummary(params).subscribe({
      next: (res: any) => {

        const terms = res?.payment_terms || [];

        this.paymentTerms = terms.map((t: any) => ({
          term: t.payment_term,
          value: t.value,
          count: t.count
        }));

      },
      error: () => {
        this.paymentTerms = [];
      }
    });
  }

  partDeviationList: any[] = [];
  showPartDeviationTopFilter = false;
  selectedPartDeviationTopN = 5;

  togglePartDeviationTopFilter() {
    this.showPartDeviationTopFilter = !this.showPartDeviationTopFilter;
  }
  loadPartPricingSummary() {

    // Get engineer_id and manager_id from sessionStorage
    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      project_id: Number(this.project_id),
      version_id: Number(this.version_id),
      top_n: this.selectedPartDeviationTopN
    };


    this.masterService.getPartPricing(params).subscribe({
      next: (res: any) => {

        this.partDeviationList = res?.parts || [];
        console.log('✓ Part Deviation List loaded:', this.partDeviationList);

      },
      error: (err) => {
        console.error('✗ Error loading part deviation:', err);
        this.partDeviationList = [];
      }
    });
  }
  getBestMinimum(row: any): number {

    const min = Number(row.minimumPrice);

    if (row.minimumPriceFromPO !== 'NA') {
      const po = Number(row.minimumPriceFromPO);
      return Math.min(min, po);
    }

    return min;
  }

  // ===== DATE FILTER PROPERTIES =====
  showCostDateFilter = false;
  showOfflineOnlineDateFilter = false;

  costStartDate: string | null = null;
  costEndDate: string | null = null;

  offlineOnlineStartDate: string | null = null;
  offlineOnlineEndDate: string | null = null;

  // ===== DATE FILTER TOGGLE METHODS =====
  toggleCostDateFilter() {
    this.showCostDateFilter = !this.showCostDateFilter;
  }

  cancelCostDateFilter() {
    this.costStartDate = null;
    this.costEndDate = null;
    this.showCostDateFilter = false;
    this.loadCostByCurrency();
  }

  toggleOfflineOnlineDateFilter() {
    this.showOfflineOnlineDateFilter = !this.showOfflineOnlineDateFilter;
  }

  cancelOfflineOnlineDateFilter() {
    this.offlineOnlineStartDate = null;
    this.offlineOnlineEndDate = null;
    this.showOfflineOnlineDateFilter = false;
    this.getOfflineOnlineSummary();
  }

  showQtyDiffTopFilter = false;
  selectedQtyDiffTopN = 5;

  toggleQtyDiffTopFilter() {
    this.showQtyDiffTopFilter = !this.showQtyDiffTopFilter;
  }
  loadQuantityDiff() {

    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      project_id: Number(this.project_id),
      version_id: Number(this.version_id),
      top_n: this.selectedQtyDiffTopN
    };

    this.masterService.getQuantityDiff(params).subscribe({
      next: (res: any) => {


        this.partDetails = res;

        console.log('Quantity diff bind', this.partDetails);

      },
      error: (err) => {
        console.error('Quantity diff api failed', err);
      }
    });

  }
  showPartLtTopFilter = false;
  selectedPartLtTopN = 5;

  togglePartLtTopFilter() {
    this.showPartLtTopFilter = !this.showPartLtTopFilter;
  }
  partLeadtimeList: any[] = [];

  loadPartLeadtime() {

    const engineer_id = sessionStorage.getItem('engineer_id');
    const manager_id = sessionStorage.getItem('manager_id');
    const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');

    const params: any = {
      manager_id: (manager_id || currentUser.manager_id) ?? currentUser.user_id,
      engineer_id: engineer_id || currentUser.engineer_id,
      project_id: Number(this.project_id),
      version_id: Number(this.version_id),
      top_n: this.selectedQtyDiffTopN
    };
    this.masterService.getHierarchyLeadtime(params).subscribe({
      next: (res: any) => {
        this.partLeadtimeList = res || [];
        console.log('✓ Part Leadtime List loaded:', this.partLeadtimeList);
      },
      error: (err) => {
        console.error('✗ Error loading part leadtime:', err);
        this.partLeadtimeList = [];
      }
    });

  }

  navigateToPartInProjectView(event: any, partNumber: string): void {
    console.log("✓ navigateToPartInProjectView called with part:", partNumber);

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!partNumber) {
      console.warn("⚠️ Part number is empty");
      return;
    }

    console.log("✓ Emitting partSelected event with part:", partNumber);

    // Emit the event to parent component (project-view)
    this.partSelected.emit(partNumber);
  }


  onCurrencyClick(currency: string): void {
    console.log("✓ onCurrencyClick called with currency:", currency);

    if (!currency) {
      console.warn("⚠️ Currency is empty");
      return;
    }

    console.log("✓ Emitting currencySelected event with currency:", currency);

    // Emit the event to parent component (project-view)
    this.currencySelected.emit(currency);
  }

  onVendorClick(vendor: any): void {
    console.log("✓ onVendorClick called with vendor:", vendor);

    if (!vendor || !vendor.supplier) {
      console.warn("⚠️ Vendor or supplier name is empty");
      return;
    }

    console.log("✓ Emitting vendorSelected event with vendor:", vendor.supplier);

    // Emit the event to parent component (project-view)
    this.vendorSelected.emit(vendor.supplier);
  }
}